/**
 * DashboardPage — Context-Aware Dashboard Router
 *
 * Renders the correct dashboard based on user context:
 * - Tier 1: Platform Dashboard (super_admin, no tenant context)
 * - Tier 2: Tenant Admin Dashboard (tenant_admin in tenant context)
 * - Tier 3: Tenant User Landing (regular user in tenant context)
 *
 * WQ-086: Tenant Dashboard Architecture (3-Tier)
 */

import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAdminContext } from '../contexts/AdminContextProvider';
import TenantAdminDashboard from '../features/tenants/components/TenantAdminDashboard';
import TenantUserLanding from '../features/tenants/components/TenantUserLanding';
import FunctionalAreaDashboard from '../features/tenants/components/FunctionalAreaDashboard';
import { getAreaConfig } from '../features/tenants/config/functionalAreaConfigs';
import { useDashboardMetrics } from '../features/dashboard/hooks/useDashboardMetrics';
import type { DashboardMetrics } from '../features/dashboard/types';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ActivityIcon,
  UsersIcon,
  ServerIcon,
  ZapIcon,
} from '../components/common/Icons';

// Phase 3.5-B: Multi-Tenant Visibility (SESSION-E)
import {
  HealthMatrix,
  IsolationChart,
  VersionChart,
} from '../components/dashboard/widgets';
import { useTenantHealthMatrix } from '../features/dashboard/hooks/useTenantHealthMatrix';

// Phase 3.5-C: Operational Intelligence (SESSION-F)
import {
  ActivityStream,
  LawsCompliancePanel,
  SQAComplianceCard,
} from '../components/dashboard/widgets';
import { HealthDetailModal } from '../components/dashboard/HealthDetailModal';

// Phase 3.5-D: Dashboard Infrastructure Stubs
import {
  ResourceUtilizationChart,
  UpgradePipelineChart,
} from '../components/dashboard/widgets';
import { useLawsCompliance } from '../features/dashboard/hooks/useLawsCompliance';


// =============================================================================
// Tier 1: Platform Dashboard (existing — no changes to content)
// =============================================================================

function buildStats(data: DashboardMetrics) {
  return [
    {
      id: 'health',
      label: 'Platform Health',
      value: `${data.platformHealth.overall}`,
      unit: '/100',
      change: `Grade ${data.platformHealth.grade}`,
      changeType: data.platformHealth.overall >= 80 ? 'positive' : 'negative',
      icon: ActivityIcon
    },
    {
      id: 'tenants',
      label: 'Active Tenants',
      value: `${data.activeTenants.active}/${data.activeTenants.total}`,
      unit: '',
      change: data.activeTenants.trend.change === 0
        ? 'Stable'
        : `${data.activeTenants.trend.change > 0 ? '+' : ''}${data.activeTenants.trend.change}`,
      changeType: data.activeTenants.trend.change >= 0 ? 'positive' : 'negative',
      icon: UsersIcon
    },
    {
      id: 'containers',
      label: 'Containers',
      value: `${data.containerStatus.running}/${data.containerStatus.total}`,
      unit: '',
      change: data.containerStatus.running === data.containerStatus.total
        ? 'All healthy'
        : `${data.containerStatus.total - data.containerStatus.running} down`,
      changeType: data.containerStatus.running === data.containerStatus.total ? 'positive' : 'negative',
      icon: ServerIcon
    },
    {
      id: 'uptime',
      label: 'System Uptime',
      value: `${data.systemUptime.currentUptime.percentage}%`,
      unit: '',
      change: `${data.systemUptime.currentUptime.duration.days}d ${data.systemUptime.currentUptime.duration.hours}h`,
      changeType: 'positive',
      icon: ZapIcon
    }
  ];
}

const DashboardSkeleton: React.FC = () => (
  <div className="grid grid-cols-4" style={{ marginBottom: 'var(--space-6)' }}>
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="card stat-card card-elevated">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="skeleton" style={{ width: '80px', height: '32px', marginBottom: '8px', borderRadius: 'var(--radius-sm)' }} />
            <div className="skeleton" style={{ width: '100px', height: '16px', marginBottom: '8px', borderRadius: 'var(--radius-sm)' }} />
            <div className="skeleton" style={{ width: '120px', height: '14px', borderRadius: 'var(--radius-sm)' }} />
          </div>
          <div className="skeleton" style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-lg)' }} />
        </div>
      </div>
    ))}
  </div>
);

const PlatformDashboard: React.FC = () => {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error, refresh, lastUpdated } = useDashboardMetrics(authFetch, {
    refreshInterval: 30000,
  });
  const [healthModalOpen, setHealthModalOpen] = useState(false);

  const stats = useMemo(() => data ? buildStats(data) : [], [data]);

  // Phase 3.5-B: Tenant Health Matrix
  const {
    matrixData,
    isLoading: healthMatrixLoading,
    error: healthMatrixError,
    isolationDist,
    versionDist,
    filter: healthFilter,
    setFilter: setHealthFilter,
    sortBy,
    sortDirection,
    onSort,
    setPage,
  } = useTenantHealthMatrix(authFetch, { refreshInterval: 30000 });

  // WQ-246: Navigate to tenants route via React Router
  const handleTenantClick = useCallback((_tenantId: string) => {
    navigate('/tenants');
  }, [navigate]);

  // Phase 3.5-C: Laws Compliance
  const {
    data: complianceData,
    isLoading: complianceLoading,
    triggerAudit,
    isAuditing,
  } = useLawsCompliance(authFetch);

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="page-title">Welcome back, {user?.name || 'Admin'}</h2>
        {lastUpdated && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Stats Grid */}
      {isLoading && !data ? (
        <DashboardSkeleton />
      ) : error && !data ? (
        <div className="card card-elevated" style={{ textAlign: 'center', padding: 'var(--space-8)', marginBottom: 'var(--space-6)' }}>
          <h3 style={{ marginBottom: 'var(--space-2)' }}>Unable to load dashboard metrics</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>{error.message}</p>
          <button className="btn btn-primary" onClick={refresh}>
            Try Again
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-4" style={{ marginBottom: 'var(--space-6)' }}>
          {stats.map((stat) => (
            <div
              key={stat.id}
              className="card stat-card card-elevated"
              onClick={stat.id === 'health' ? () => setHealthModalOpen(true) : undefined}
              style={stat.id === 'health' ? { cursor: 'pointer' } : undefined}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--accent-primary-muted)',
                    borderRadius: 'var(--radius-lg)',
                    color: 'var(--accent-primary)',
                    flexShrink: 0,
                  }}
                >
                  <stat.icon size={24} />
                </div>
                <div>
                  <div className="stat-value">
                    {stat.value}
                    {stat.unit && <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', fontWeight: 400 }}>{stat.unit}</span>}
                  </div>
                  <div className="stat-label">{stat.label}</div>
                  <div className={`stat-change ${stat.changeType}`}>
                    {stat.changeType === 'positive' ? (
                      <ArrowUpIcon size={12} />
                    ) : (
                      <ArrowDownIcon size={12} />
                    )}
                    {stat.change}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TIER 2: Primary Visualizations */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: 'var(--space-6)',
        marginBottom: 'var(--space-6)',
      }}>
        {/* Left: Multi-Tenant Health Matrix */}
        <HealthMatrix
          tenants={matrixData?.tenants ?? []}
          metrics={matrixData?.metrics ?? []}
          summary={matrixData?.summary ?? { healthy_count: 0, warning_count: 0, critical_count: 0 }}
          pagination={matrixData?.pagination ?? { page: 1, page_size: 10, total_items: 0, total_pages: 1 }}
          isLoading={healthMatrixLoading}
          error={healthMatrixError}
          filter={healthFilter}
          onFilterChange={setHealthFilter}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={onSort}
          onPageChange={setPage}
          onTenantClick={handleTenantClick}
        />

        {/* Right: SQA Compliance (WQ-179d — swapped from bottom row) */}
        <SQAComplianceCard />
      </div>

      {/* TIER 2 Bottom: Resource Utilization + Laws Compliance + Activity Stream */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'var(--space-6)',
        marginBottom: 'var(--space-6)',
      }}>
        {/* Left: Resource Utilization (STUB-DASH-008 resolved) */}
        <div className="card card-elevated" style={{ padding: 'var(--space-4)' }}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-lg, 18px)', color: 'var(--text-primary)' }}>
            Resource Utilization
          </h3>
          <ResourceUtilizationChart
            resources={data?.containerStatus.resources ?? null}
            components={data?.systemUptime.components ?? null}
            isLoading={isLoading}
          />
        </div>

        {/* Center: Platform Laws Compliance */}
        <LawsCompliancePanel
          data={complianceData}
          isLoading={complianceLoading}
          onRunAudit={triggerAudit}
          isAuditing={isAuditing}
        />

        {/* Right: Activity Stream (WQ-179d — swapped from top row) */}
        <div className="card card-elevated" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: '400px' }}>
          <ActivityStream fillHeight />
        </div>
      </div>

      {/* TIER 2.5: Secondary Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'var(--space-4)',
      }}>
        <IsolationChart data={isolationDist} isLoading={healthMatrixLoading} />
        <VersionChart data={versionDist} isLoading={healthMatrixLoading} />
        {/* Upgrade Pipeline (STUB-DASH-009 resolved) */}
        <div className="card card-elevated" style={{ padding: 'var(--space-4)' }}>
          <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md, 16px)', color: 'var(--text-primary)' }}>
            Upgrade Pipeline
          </h4>
          <UpgradePipelineChart
            byVersion={data?.containerStatus.byVersion ?? null}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Health Detail Modal */}
      {data && (
        <HealthDetailModal
          health={data.platformHealth}
          open={healthModalOpen}
          onClose={() => setHealthModalOpen(false)}
        />
      )}
    </div>
  );
};


// =============================================================================
// Dashboard Router — selects tier based on context
// =============================================================================

/** Admin roles that see the Tenant Admin Dashboard (Tier 2) */
const ADMIN_ROLES = new Set(['tenant_admin', 'admin', 'superadmin', 'super_admin']);

const DashboardPage: React.FC = () => {
  const { user, authFetch, tenantId } = useAuth();
  const { activeTenant, isInTenantContext } = useAdminContext();

  // WQ-087b: Resolve landing area for tenant users (Tier 3)
  const [landingArea, setLandingArea] = useState<string | null>(null);
  const [landingLoaded, setLandingLoaded] = useState(false);
  const hasTenantContext = !!tenantId || isInTenantContext;

  useEffect(() => {
    if (!hasTenantContext) {
      setLandingLoaded(true);
      return;
    }
    authFetch('/api/tenant-portal/user/landing-area')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.landing_area && d.source !== 'fallback') {
          setLandingArea(d.landing_area);
        }
        setLandingLoaded(true);
      })
      .catch(() => setLandingLoaded(true));
  }, [authFetch, hasTenantContext]);

  // Tier 1: Platform dashboard when NOT in tenant context
  if (!isInTenantContext) {
    return <PlatformDashboard />;
  }

  // Tier 2: Tenant admin dashboard for admin roles in tenant context
  const userRole = user?.role?.toLowerCase() || '';
  if (ADMIN_ROLES.has(userRole)) {
    return <TenantAdminDashboard tenant={activeTenant || undefined} />;
  }

  // Tier 3: Tenant user — show preferred area dashboard if set
  if (landingLoaded && landingArea) {
    const areaConfig = getAreaConfig(landingArea);
    if (areaConfig) {
      return <FunctionalAreaDashboard config={areaConfig} />;
    }
  }

  // Tier 3 fallback: Generic tenant user landing
  return <TenantUserLanding tenant={activeTenant || undefined} />;
};

export default DashboardPage;
